import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { api, beginAuthOperation } from "@/lib/api";
import {
  isSessionInactive,
  SESSION_ACTIVITY_STORAGE_KEY,
} from "@/lib/session-activity";
import { useAuthStore } from "@/stores/auth-store";

const AuthContext = createContext(null);
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "focus"];
const ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
const SESSION_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const { user, isGuest, setUser, clearSession, enterAsGuest } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const sessionUserId = user?.id;
  const authOperationVersionRef = useRef(0);

  useEffect(() => {
    let active = true;
    const bootstrapVersion = authOperationVersionRef.current;

    const bootstrapAuth = async () => {
      if (isGuest) {
        if (active) {
          setLoading(false);
        }
        return;
      }

      try {
        const response = await api.get("/usuarios/me");
        if (!active || bootstrapVersion !== authOperationVersionRef.current) {
          return;
        }
        setUser(response.data);
      } catch (error) {
        if (active && bootstrapVersion === authOperationVersionRef.current) {
          window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
          clearSession();
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    bootstrapAuth();

    return () => {
      active = false;
    };
  }, [clearSession, isGuest, setUser]);

  useEffect(() => {
    const handleSessionRefreshed = (event) => {
      if (event.detail) {
        setUser(event.detail);
      }
    };
    const handleSessionExpired = () => {
      authOperationVersionRef.current += 1;
      window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      clearSession();
    };

    window.addEventListener("polenica:session-refreshed", handleSessionRefreshed);
    window.addEventListener("polenica:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("polenica:session-refreshed", handleSessionRefreshed);
      window.removeEventListener("polenica:session-expired", handleSessionExpired);
    };
  }, [clearSession, setUser]);

  useEffect(() => {
    if (!sessionUserId || isGuest) {
      return undefined;
    }

    let lastWriteAt = 0;
    let lastHeartbeatAt = Date.now();
    let heartbeatInFlight = false;
    let expired = false;

    const closeInactiveSession = () => {
      if (expired) {
        return;
      }

      expired = true;
      window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      clearSession();
      api.post("/auth/logout").catch(() => undefined);
      window.dispatchEvent(new CustomEvent("polenica:session-expired"));
    };

    const expireInactiveSession = () => {
      if (expired) {
        return;
      }
      const lastActivityAt = window.localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY);
      if (!isSessionInactive(lastActivityAt)) {
        return;
      }

      closeInactiveSession();
    };

    const sendSessionHeartbeat = (now) => {
      if (
        heartbeatInFlight ||
        now - lastHeartbeatAt < SESSION_HEARTBEAT_INTERVAL_MS
      ) {
        return;
      }

      heartbeatInFlight = true;
      lastHeartbeatAt = now;
      api
        .get("/usuarios/me")
        .catch(() => undefined)
        .finally(() => {
          heartbeatInFlight = false;
        });
    };

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
        return;
      }

      lastWriteAt = now;
      const lastActivityAt = window.localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY);
      if (isSessionInactive(lastActivityAt, now)) {
        closeInactiveSession();
        return;
      }

      window.localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(now));
      sendSessionHeartbeat(now);
    };

    recordActivity();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    const intervalId = window.setInterval(expireInactiveSession, ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.clearInterval(intervalId);
    };
  }, [clearSession, isGuest, sessionUserId]);

  const checkAuth = async () => {
    if (isGuest) {
      return null;
    }

    try {
      const response = await api.get("/usuarios/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      clearSession();
      return null;
    }
  };

  const login = async (email, password) => {
    beginAuthOperation();
    const operationVersion = authOperationVersionRef.current + 1;
    authOperationVersionRef.current = operationVersion;
    const response = await api.post("/auth/login", { email, password });
    if (operationVersion === authOperationVersionRef.current) {
      window.localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(Date.now()));
      setUser(response.data.user);
    }
    return response.data;
  };

  const register = async (payload) => {
    beginAuthOperation();
    const operationVersion = authOperationVersionRef.current + 1;
    authOperationVersionRef.current = operationVersion;
    const response = await api.post("/auth/register", payload);
    if (operationVersion === authOperationVersionRef.current) {
      window.localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(Date.now()));
      setUser(response.data.user);
    }
    return response.data;
  };

  const logout = async () => {
    beginAuthOperation();
    authOperationVersionRef.current += 1;
    try {
      await api.post("/auth/logout");
    } finally {
      window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
      clearSession();
    }
  };

  const accessAsGuest = () => {
    beginAuthOperation();
    authOperationVersionRef.current += 1;
    window.localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY);
    enterAsGuest();
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isGuest,
        loading,
        login,
        register,
        logout,
        checkAuth,
        accessAsGuest,
        clearSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return value;
}
